/**
 * AdminRetailerEditModal
 *
 * Single modal for both create and edit. On create, choose how to attach
 * products (All / Select / None). On edit, manage which products are
 * carried, see the eligibility status, and (for non-MSRP retailers) delete
 * with name-typed confirmation.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Save, Loader2, Plus, Trash2, AlertTriangle, Building2, ShieldCheck, ShieldAlert,
  BellOff, Power, Percent, Sparkles, ChevronDown, ChevronRight, ExternalLink,
  Download, Upload,
} from 'lucide-react';
import { ImageUploadField, AspectPreset } from '../../components/admin/ImageUploadField';
import { Tooltip } from '../../components/ui/Tooltip';
import { AdminImportModal } from './AdminImportModal';
import { fetchRetailerExport } from '../../services/adminProductsApi';
import { downloadWorkbook, RETAILER_COLUMNS } from '../../services/productSheet';
import {
  AdminRetailer,
  CurrencyCode,
  RetailerCreatePayload,
  RetailerNavLink,
  RetailerPricingInfo,
  RetailerProductRow,
  RetailerProductUpdate,
  RetailerFieldDef,
  RetailerSplashConfig,
  RetailerTheme,
  brandIconFor,
  createAdminRetailer,
  fetchAdminRetailer,
  fetchAdminRetailers,
  patchAdminRetailer,
} from '../../services/adminRetailersApi';
import type { CaptureMode } from '../../config/capturePolicy';
import { fetchAdminLocales, type AdminLocale } from '../../services/adminLocalesApi';
import { RetailerAccountsSection } from './RetailerAccountsSection';

interface Props {
  retailerId: number | null;
  isCreating: boolean;
  /** Prefill for the create form's name (e.g. a Salesforce account name from the members queue). */
  initialName?: string;
  /** Create as a sub-retailer of this retailer (the workspace's "+ Sub-retailer" button). */
  initialParentId?: number;
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}

const blankRetailer = (): AdminRetailer => ({
  id: 0,
  name: '',
  addressLine1: null,
  addressLine2: null,
  city: null,
  stateProvince: null,
  postalCode: null,
  country: null,
  phone: null,
  email: null,
  website: null,
  latitude: null,
  longitude: null,
  isNational: false,
  isActive: true,
  parentRetailerId: null,
  pricingSource: 'own',
  emailDomains: [],
  sfAccounts: [],
  currenciesSupported: ['CAD', 'USD'],
  showTotals: false,
  totalFormat: 'rounded',
  defaultLocale: null,
  localesSupported: null,
  captureMode: 'full',
  pardotFormHandlerUrl: null,
  pardotOnBehalfHandlerUrl: null,
  accountsEnabled: true,
  subdomain: null,
  isPartner: false,
  theme: null,
  logoUrl: null,
  logoAlt: null,
  brandIconUrl: null,
  splashConfig: null,
  navLinks: null,
  underConstructionMessage: null,
  mapFilter: null,
});

/** Brand icon: square only. */
const BRAND_ICON_ASPECTS: AspectPreset[] = [{ label: 'Square', aspect: 1 }];
const BRAND_ICON_HELP =
  'A small square mark that shows beside this retailer\'s estimates in the admin lists. ' +
  'Square, at least 64×64 and at most 512×512 pixels; PNG, SVG, JPG or WebP. ' +
  'A larger raster image can be cropped square and is downsized automatically.';

/**
 * Default Trusscore brand palette. Shown as fallbacks in the theme color
 * pickers so partners know what each slot maps to. Cleared values fall
 * back to these at runtime via CSS custom property fallbacks.
 */
const DEFAULT_THEME: Required<RetailerTheme> = {
  // Nav
  navBg: '#3a4b5c',
  navFg: '#ffffff',
  navHoverBg: '#2d3b4a',
  // Brand
  primary: '#FEB100',
  accent: '#FEB100',
  // Primary buttons
  buttonBg: '#FEB100',
  buttonFg: '#222222',
  // Secondary buttons (slate)
  secondaryBg: '#3a4b5c',
  secondaryFg: '#ffffff',
  // Modal title bars
  modalHeaderBg: '#FEB100',
  modalHeaderFg: '#1a1a2e',
  // Bill of Materials table
  tableHeaderBg: '#3a4b5c',
  tableHeaderFg: '#ffffff',
  tableTotalBg: '#f1f5f9',
  tableTotalFg: '#3a4b5c',
  // Cost total card
  estimateBg: '#3a4b5c',
  estimateValueColor: '#FEB100',
  estimateValueAltColor: '#ffffff',
  // Footer
  footerBg: '#1f2937',
  footerFg: '#ffffff',
  footerMutedFg: '#6b7280',
};

const LOGO_ASPECTS: AspectPreset[] = [
  { label: 'Free',     aspect: undefined },
  { label: 'Wide 4:1', aspect: 4 / 1 },
  { label: 'Square',   aspect: 1 },
];

const SPLASH_ASPECTS: AspectPreset[] = [
  { label: 'Free',  aspect: undefined },
  { label: '16:9',  aspect: 16 / 9 },
  { label: '4:3',   aspect: 4 / 3 },
  { label: 'Square', aspect: 1 },
];

interface ThemeField {
  key: keyof RetailerTheme;
  label: string;
  help: string;
}

interface ThemeGroup {
  label: string;
  description?: string;
  fields: ThemeField[];
}

/**
 * Theme color pickers grouped into logical sections so the admin form
 * stays scannable as the palette grows. Each group renders as its own
 * sub-heading + grid of swatches.
 */
const THEME_GROUPS: ThemeGroup[] = [
  {
    label: 'Navigation',
    fields: [
      { key: 'navBg',      label: 'Nav background',     help: 'Top navigation bar background' },
      { key: 'navFg',      label: 'Nav foreground',     help: 'Top nav text and icon color' },
      { key: 'navHoverBg', label: 'Nav hover bg',       help: 'Hover state for nav menu items' },
    ],
  },
  {
    label: 'Brand',
    description: 'Drives selected-state borders, halos, and most highlight tints across the wizard.',
    fields: [
      { key: 'primary', label: 'Primary color', help: 'Selection borders, active step circle, value emphasis' },
      { key: 'accent',  label: 'Accent color',  help: 'Links and secondary highlights' },
    ],
  },
  {
    label: 'Primary buttons',
    description: 'Next, Submit, and other primary actions.',
    fields: [
      { key: 'buttonBg', label: 'Button background', help: 'Primary action background' },
      { key: 'buttonFg', label: 'Button foreground', help: 'Primary action text' },
    ],
  },
  {
    label: 'Secondary buttons',
    description: 'Slate-style buttons (Back to Edit, Start Over, Cancel).',
    fields: [
      { key: 'secondaryBg', label: 'Secondary background', help: 'Slate-style button bg + outline color' },
      { key: 'secondaryFg', label: 'Secondary foreground', help: 'Slate-style button text' },
    ],
  },
  {
    label: 'Modal headers',
    description: 'Title bars on modals like “Detailed Material List Request”.',
    fields: [
      { key: 'modalHeaderBg', label: 'Modal header bg', help: 'Modal title bar background' },
      { key: 'modalHeaderFg', label: 'Modal header fg', help: 'Modal title text' },
    ],
  },
  {
    label: 'Bill of Materials tables',
    description: 'Table head row and the lighter total row at the bottom of each section.',
    fields: [
      { key: 'tableHeaderBg', label: 'Header row bg', help: 'Table head background (slate by default)' },
      { key: 'tableHeaderFg', label: 'Header row fg', help: 'Table head text' },
      { key: 'tableTotalBg',  label: 'Total row bg',  help: 'Section total row background' },
      { key: 'tableTotalFg',  label: 'Total row fg',  help: 'Section total row text' },
    ],
  },
  {
    label: 'Estimated Total card',
    description: 'The dark card with the estimate value. The value animates between two colors on first paint.',
    fields: [
      { key: 'estimateBg',            label: 'Card background',          help: 'Background of the Estimated Total card' },
      { key: 'estimateValueColor',    label: 'Value primary color',      help: 'Cost figure color (start/end of the glow animation)' },
      { key: 'estimateValueAltColor', label: 'Value alternate color',    help: 'Cost figure color at the midpoint of the glow animation' },
    ],
  },
  {
    label: 'Footer',
    fields: [
      { key: 'footerBg',      label: 'Footer background',  help: 'Footer band background' },
      { key: 'footerFg',      label: 'Footer foreground',  help: 'Copyright, links, and the expand handle on hover' },
      { key: 'footerMutedFg', label: 'Footer muted text',  help: 'Disclaimer, build line, expand handle, and divider rules' },
    ],
  },
];

type ProductMode = 'all' | 'subset' | 'none';

export const AdminRetailerEditModal: React.FC<Props> = ({
  retailerId, isCreating, initialName, initialParentId, canEdit, onClose, onSaved, onDeleted,
}) => {
  const [retailer, setRetailer] = useState<AdminRetailer | null>(
    isCreating ? { ...blankRetailer(), name: initialName ?? '', parentRetailerId: initialParentId ?? null } : null,
  );
  const [products, setProducts] = useState<RetailerProductRow[]>([]);
  const [fieldDefs, setFieldDefs] = useState<RetailerFieldDef[]>([]);
  // Per-retailer custom field edits: productId → { fieldDefId → value }.
  const [customFieldEdits, setCustomFieldEdits] = useState<Map<number, Record<number, string>>>(new Map());
  const [requiredAcronyms, setRequiredAcronyms] = useState<string[]>([]);
  const [usage, setUsage] = useState<{ userCount: number; shareCount: number }>({ userCount: 0, shareCount: 0 });
  /** Active languages, for the portal language settings. Empty until loaded. */
  const [locales, setLocales] = useState<AdminLocale[]>([]);
  /** Every retailer, for the parent-retailer picker. Empty until loaded. */
  const [allRetailers, setAllRetailers] = useState<AdminRetailer[]>([]);
  /** Which book the product grid is showing (the parent's when inheriting). */
  const [pricingInfo, setPricingInfo] = useState<RetailerPricingInfo | null>(null);

  const [loading, setLoading] = useState(!isCreating);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create-flow product attach mode + selected ids when in subset.
  const [createMode, setCreateMode] = useState<ProductMode>('all');
  const [createSelected, setCreateSelected] = useState<Set<number>>(new Set());

  // Edit-flow tracks which carried-flags have been toggled relative to the
  // server snapshot. Final patch is computed from this on save.
  const [carriedDraft, setCarriedDraft] = useState<Map<number, boolean>>(new Map());

  // Per-row pricing / active / needs_review / markup edits. Keyed by
  // productId. Only fields the user touched are present, so we know what
  // to send. The `markupClearedBy*` flags are UI-only — they let us show
  // a "markup cleared by manual price" tag inline until save.
  interface RowEdit {
    priceUsd?: number;
    priceCad?: number;
    isActive?: boolean;
    needsReview?: boolean;
    /**
     * Per-currency percent markup over MSRP. `undefined` = untouched,
     * a number sets it (and resets price to MSRP × (1 + N/100) on save),
     * `null` clears the markup with no price change.
     */
    percentMarkupUsd?: number | null;
    percentMarkupCad?: number | null;
    markupClearedByPriceUsd?: boolean;
    markupClearedByPriceCad?: boolean;
  }
  const [rowEdits, setRowEdits] = useState<Map<number, RowEdit>>(new Map());

  const patchRow = (productId: number, patch: RowEdit) => {
    setRowEdits(prev => {
      const next = new Map(prev);
      const merged = { ...(next.get(productId) ?? {}), ...patch };
      next.set(productId, merged);
      return next;
    });
  };

  const patchCustomField = (productId: number, fieldDefId: number, value: string) => {
    setCustomFieldEdits(prev => {
      const next = new Map(prev);
      const merged = { ...(next.get(productId) ?? {}), [fieldDefId]: value };
      next.set(productId, merged);
      return next;
    });
  };

  /**
   * Manual price edit handler — also clears that currency's markup if
   * one is set on the row. Surfaces the "cleared by price" inline tag.
   */
  const editManualPrice = (productId: number, currency: CurrencyCode, value: number) => {
    setRowEdits(prev => {
      const existing = prev.get(productId) ?? {};
      const row = products.find(p => p.productId === productId);
      const next = new Map(prev);
      // Effective markup *right now* (server value, then any drafted edit on top).
      const draftedMarkup = currency === 'USD' ? existing.percentMarkupUsd : existing.percentMarkupCad;
      const serverMarkup  = currency === 'USD' ? row?.percentMarkupUsd    : row?.percentMarkupCad;
      const effective = draftedMarkup !== undefined ? draftedMarkup : (serverMarkup ?? null);
      const merged: RowEdit = { ...existing };
      if (currency === 'USD') merged.priceUsd = value;
      else                    merged.priceCad = value;
      if (effective !== null) {
        // Manual edit clears markup for this currency.
        if (currency === 'USD') {
          merged.percentMarkupUsd = null;
          merged.markupClearedByPriceUsd = true;
        } else {
          merged.percentMarkupCad = null;
          merged.markupClearedByPriceCad = true;
        }
      }
      next.set(productId, merged);
      return next;
    });
  };

  // Filter for the product list inside the modal.
  const [productFilter, setProductFilter] = useState('');
  const [priceImportOpen, setPriceImportOpen] = useState(false);
  const [exportingPrices, setExportingPrices] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteNameInput, setDeleteNameInput] = useState('');

  // Pre-save markup confirmation. Computed once per save attempt, then
  // cleared after the user accepts (so we don't ask again on the same
  // save). null → no pending confirmation.
  const [pendingMarkupConfirm, setPendingMarkupConfirm] = useState<{
    rows: Array<{ productId: number; sku: string; usd: number | null; cad: number | null }>;
  } | null>(null);

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (isCreating) return;
    if (!retailerId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const detail = await fetchAdminRetailer(retailerId);
        if (cancelled) return;
        setRetailer(detail.retailer);
        setProducts(detail.products);
        setPricingInfo(detail.pricing ?? null);
        setFieldDefs(detail.fieldDefs ?? []);
        setRequiredAcronyms(detail.requiredAcronyms);
        setUsage(detail.usage);
        // Hydrate the draft to match server state — toggles below diff against this.
        const draft = new Map<number, boolean>();
        detail.products.forEach(p => draft.set(p.productId, p.carried));
        setCarriedDraft(draft);
        setRowEdits(new Map());
        setCustomFieldEdits(new Map());
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [retailerId, isCreating]);

  useEffect(() => {
    if (!isCreating) return;
    // For "Add" flow we need the master product list to render the subset
    // picker. Reuse the detail fetch on retailer #1 which already returns
    // the master catalog.
    let cancelled = false;
    (async () => {
      try {
        const detail = await fetchAdminRetailer(1);
        if (cancelled) return;
        setProducts(detail.products);
        setRequiredAcronyms(detail.requiredAcronyms);
        // Default-all-selected for "subset" mode.
        setCreateSelected(new Set(detail.products.filter(p => p.productActive).map(p => p.productId)));
      } catch {
        /* swallowed — modal still works for retailer info even if catalog fails */
      }
    })();
    return () => { cancelled = true; };
  }, [isCreating]);

  useEffect(() => {
    let cancelled = false;
    fetchAdminLocales()
      .then(list => { if (!cancelled) setLocales(list.filter(l => l.isActive)); })
      .catch(() => { /* language block renders its "unavailable" note */ });
    fetchAdminRetailers()
      .then(data => { if (!cancelled) setAllRetailers(data.retailers); })
      .catch(() => { /* parent picker just shows no options */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setVisible(false);
        setTimeout(onClose, 250);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 250);
  };

  const updateField = <K extends keyof AdminRetailer>(key: K, value: AdminRetailer[K]) => {
    setRetailer(prev => prev ? { ...prev, [key]: value } : prev);
  };

  // Re-pull server state after a bulk price import so the grid reflects it.
  const reloadDetail = async () => {
    if (!retailerId) return;
    try {
      const detail = await fetchAdminRetailer(retailerId);
      setRetailer(detail.retailer);
      setProducts(detail.products);
      const draft = new Map<number, boolean>();
      detail.products.forEach(p => draft.set(p.productId, p.carried));
      setCarriedDraft(draft);
      setRowEdits(new Map());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reload');
    }
  };

  const handleExportPrices = async () => {
    if (!retailerId || !retailer) return;
    setExportingPrices(true);
    setError(null);
    try {
      const data = await fetchRetailerExport(retailerId);
      const date = new Date().toISOString().slice(0, 10);
      const slug = retailer.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'retailer';
      downloadWorkbook(RETAILER_COLUMNS, data.products as unknown as Record<string, unknown>[], `${slug}-prices-${date}.xlsx`, 'Prices');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExportingPrices(false);
    }
  };

  // Eligibility computed from the *draft* state so the badge updates as you
  // toggle products. Matches the server formula.
  const draftEligibility = useMemo(() => {
    if (requiredAcronyms.length === 0) return { eligible: true, missing: [] as string[] };
    const carriedAcronyms = new Set<string>();
    if (isCreating) {
      const targetIds = createMode === 'all'
        ? new Set(products.filter(p => p.productActive).map(p => p.productId))
        : createMode === 'subset'
          ? createSelected
          : new Set<number>();
      products.forEach(p => {
        if (p.productActive && targetIds.has(p.productId)) carriedAcronyms.add(p.acronym);
      });
    } else {
      products.forEach(p => {
        if (!p.productActive) return;
        if (!carriedDraft.get(p.productId)) return;
        // Eligibility requires is_active=1 — apply per-row drafted active
        // override if present so the badge reflects what saving will do.
        const drafted = rowEdits.get(p.productId)?.isActive;
        const effectiveActive = drafted !== undefined ? drafted : !!p.isActive;
        if (effectiveActive) carriedAcronyms.add(p.acronym);
      });
    }
    const missing = requiredAcronyms.filter(a => !carriedAcronyms.has(a));
    return { eligible: missing.length === 0, missing };
  }, [requiredAcronyms, isCreating, createMode, createSelected, products, carriedDraft, rowEdits]);

  const filteredProducts = useMemo(() => {
    const q = productFilter.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p =>
      p.sku.toLowerCase().includes(q) ||
      p.name.toLowerCase().includes(q) ||
      p.acronym.toLowerCase().includes(q) ||
      p.color.toLowerCase().includes(q),
    );
  }, [products, productFilter]);

  const validate = (): string | null => {
    if (!retailer) return 'No retailer loaded';
    if (!retailer.name.trim()) return 'Name is required';
    return null;
  };

  /**
   * Build the list of rows that will have their stored price reset to
   * MSRP × (1 + markup/100) when this save runs. Used to populate the
   * pre-save confirmation dialog. Only set-to-numeric markup edits cause
   * a price reset; null clears do not.
   */
  const collectMarkupResetRows = (): Array<{ productId: number; sku: string; usd: number | null; cad: number | null }> => {
    const out: Array<{ productId: number; sku: string; usd: number | null; cad: number | null }> = [];
    products.forEach(p => {
      const edit = rowEdits.get(p.productId);
      if (!edit) return;
      const usdSet = edit.percentMarkupUsd !== undefined && edit.percentMarkupUsd !== null;
      const cadSet = edit.percentMarkupCad !== undefined && edit.percentMarkupCad !== null;
      if (!usdSet && !cadSet) return;
      out.push({
        productId: p.productId,
        sku: p.sku,
        usd: usdSet ? (edit.percentMarkupUsd as number) : null,
        cad: cadSet ? (edit.percentMarkupCad as number) : null,
      });
    });
    return out;
  };

  const handleSave = async () => {
    if (!canEdit || !retailer) return;
    const err = validate();
    if (err) { setError(err); return; }

    // Markup-set edits trigger a price reset — gate on a confirmation.
    if (!pendingMarkupConfirm) {
      const resets = collectMarkupResetRows();
      if (resets.length > 0) {
        setPendingMarkupConfirm({ rows: resets });
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      if (isCreating) {
        const payload: RetailerCreatePayload = {
          retailer: {
            name: retailer.name.trim(),
            addressLine1: retailer.addressLine1,
            addressLine2: retailer.addressLine2,
            city: retailer.city,
            stateProvince: retailer.stateProvince,
            postalCode: retailer.postalCode,
            country: retailer.country,
            phone: retailer.phone,
            email: retailer.email,
            website: retailer.website,
            latitude: retailer.latitude,
            longitude: retailer.longitude,
            isNational: retailer.isNational,
            isActive: retailer.isActive,
            parentRetailerId: retailer.parentRetailerId,
            pricingSource: retailer.pricingSource,
            emailDomains: retailer.emailDomains,
            sfAccounts: retailer.sfAccounts,
            currenciesSupported: retailer.currenciesSupported,
            showTotals: retailer.showTotals,
            totalFormat: retailer.totalFormat,
            defaultLocale: retailer.defaultLocale,
            localesSupported: retailer.localesSupported,
            captureMode: retailer.captureMode,
            pardotFormHandlerUrl: retailer.pardotFormHandlerUrl,
            pardotOnBehalfHandlerUrl: retailer.pardotOnBehalfHandlerUrl,
            accountsEnabled: retailer.accountsEnabled,
            subdomain: retailer.subdomain,
            isPartner: retailer.isPartner,
            theme: retailer.theme,
            logoUrl: retailer.logoUrl,
            logoAlt: retailer.logoAlt,
            brandIconUrl: retailer.brandIconUrl,
            splashConfig: retailer.splashConfig,
            navLinks: retailer.navLinks,
            underConstructionMessage: retailer.underConstructionMessage,
            mapFilter: retailer.mapFilter,
          },
          products:
            retailer.pricingSource === 'parent' ? { mode: 'none' }
          : createMode === 'all'   ? { mode: 'all' }
          : createMode === 'none'  ? { mode: 'none' }
                                   : { mode: 'subset', productIds: Array.from(createSelected) },
        };
        await createAdminRetailer(payload);
      } else if (retailerId) {
        // Diff carriedDraft vs server snapshot to compute add/remove arrays.
        const add: number[] = [];
        const remove: number[] = [];
        products.forEach(p => {
          const drafted = carriedDraft.get(p.productId) ?? p.carried;
          if (drafted && !p.carried) add.push(p.productId);
          if (!drafted && p.carried) remove.push(p.productId);
        });

        // Per-row edits — only for products still carried after the diff.
        // (Editing a row we're about to remove is wasted work.)
        const carriedFinal = new Set<number>();
        products.forEach(p => {
          const drafted = carriedDraft.get(p.productId) ?? p.carried;
          if (drafted) carriedFinal.add(p.productId);
        });
        const update: RetailerProductUpdate[] = [];
        rowEdits.forEach((edit, productId) => {
          if (!carriedFinal.has(productId)) return;
          const payload: RetailerProductUpdate = { productId };
          let touched = false;
          if (edit.priceUsd        !== undefined) { payload.priceUsd        = edit.priceUsd;        touched = true; }
          if (edit.priceCad        !== undefined) { payload.priceCad        = edit.priceCad;        touched = true; }
          if (edit.isActive        !== undefined) { payload.isActive        = edit.isActive;        touched = true; }
          if (edit.needsReview     !== undefined) { payload.needsReview     = edit.needsReview;     touched = true; }
          if (edit.percentMarkupUsd !== undefined) { payload.percentMarkupUsd = edit.percentMarkupUsd; touched = true; }
          if (edit.percentMarkupCad !== undefined) { payload.percentMarkupCad = edit.percentMarkupCad; touched = true; }
          if (touched) update.push(payload);
        });

        // Merge per-retailer custom field edits into the update payloads,
        // sending only values that changed from the server snapshot.
        customFieldEdits.forEach((defMap, productId) => {
          if (!carriedFinal.has(productId)) return;
          const product = products.find(p => p.productId === productId);
          const changed: Array<{ fieldDefId: number; value: string | null }> = [];
          Object.entries(defMap).forEach(([defIdStr, value]) => {
            const defId = Number(defIdStr);
            const original = product?.customFields?.find(cf => cf.fieldDefId === defId)?.value ?? '';
            if ((value ?? '') !== (original ?? '')) {
              changed.push({ fieldDefId: defId, value: value === '' ? null : value });
            }
          });
          if (changed.length === 0) return;
          let payload = update.find(u => u.productId === productId);
          if (!payload) { payload = { productId }; update.push(payload); }
          payload.customFields = changed;
        });

        const productsOps = (add.length || remove.length || update.length)
          ? { add, remove, update }
          : undefined;

        await patchAdminRetailer(retailerId, {
          retailer: {
            name: retailer.name.trim(),
            addressLine1: retailer.addressLine1,
            addressLine2: retailer.addressLine2,
            city: retailer.city,
            stateProvince: retailer.stateProvince,
            postalCode: retailer.postalCode,
            country: retailer.country,
            phone: retailer.phone,
            email: retailer.email,
            website: retailer.website,
            latitude: retailer.latitude,
            longitude: retailer.longitude,
            isNational: retailer.isNational,
            isActive: retailer.isActive,
            parentRetailerId: retailer.parentRetailerId,
            pricingSource: retailer.pricingSource,
            emailDomains: retailer.emailDomains,
            sfAccounts: retailer.sfAccounts,
            currenciesSupported: retailer.currenciesSupported,
            showTotals: retailer.showTotals,
            totalFormat: retailer.totalFormat,
            defaultLocale: retailer.defaultLocale,
            localesSupported: retailer.localesSupported,
            captureMode: retailer.captureMode,
            pardotFormHandlerUrl: retailer.pardotFormHandlerUrl,
            pardotOnBehalfHandlerUrl: retailer.pardotOnBehalfHandlerUrl,
            accountsEnabled: retailer.accountsEnabled,
            subdomain: retailer.subdomain,
            isPartner: retailer.isPartner,
            theme: retailer.theme,
            logoUrl: retailer.logoUrl,
            logoAlt: retailer.logoAlt,
            brandIconUrl: retailer.brandIconUrl,
            splashConfig: retailer.splashConfig,
            navLinks: retailer.navLinks,
            underConstructionMessage: retailer.underConstructionMessage,
            mapFilter: retailer.mapFilter,
          },
          // An inheriting retailer has no book of its own; the grid is read-only
          // so there is nothing to send, and the server would refuse it anyway.
          products: pricingInherited ? undefined : productsOps,
        });
      }
      onSaved();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
      setPendingMarkupConfirm(null);
    }
  };

  const isMsrp = !isCreating && retailerId === 1;
  // The grid shows the PARENT's book when this retailer inherits pricing —
  // read-only here; edits happen on the parent.
  const pricingInherited = !isCreating && !!pricingInfo?.inherited;
  const gridReadOnly = isMsrp || pricingInherited;

  // Retailer #1 (Trusscore / MSRP) has no Save button — everything else on it
  // is protected — so its brand icon saves through this single-field patch.
  const [iconSaving, setIconSaving] = useState(false);
  const saveIconOnly = async () => {
    if (!retailerId || !retailer) return;
    setIconSaving(true);
    setError(null);
    try {
      await patchAdminRetailer(retailerId, { retailer: { brandIconUrl: retailer.brandIconUrl } });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the icon');
    } finally {
      setIconSaving(false);
    }
  };
  const nameMatches = retailer && deleteNameInput.trim() === retailer.name;

  if (!retailer && !loading) return null;

  return (
    <div
      className={`modal d-block fade ${visible ? 'show' : ''}`}
      tabIndex={-1}
      style={{ transition: 'opacity 0.25s ease', backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
              <Building2 size={18} />
              {isCreating ? 'Add Retailer' : `Edit Retailer${retailer ? ` — ${retailer.name}` : ''}`}
              {!canEdit && <span className="badge text-bg-secondary ms-2">Read-only</span>}
              {isMsrp && <span className="badge text-bg-warning ms-2">MSRP — protected</span>}
            </h5>
            <button type="button" className="btn-close" onClick={handleClose} aria-label="Close" />
          </div>

          <div className="modal-body">
            {loading ? (
              <div className="text-center py-5 text-muted">
                <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
              </div>
            ) : !retailer ? null : (
              <>
                {error && (
                  <div className="alert alert-warning py-2 px-3 small d-flex align-items-center gap-2">
                    <AlertTriangle size={14} /> {error}
                  </div>
                )}

                {/* Eligibility banner */}
                <div className={`border rounded-3 p-2 mb-3 d-flex align-items-center gap-2 ${draftEligibility.eligible ? 'bg-success-subtle border-success-subtle' : 'bg-danger-subtle border-danger-subtle'}`}>
                  {draftEligibility.eligible ? (
                    <>
                      <ShieldCheck size={18} className="text-success" />
                      <strong className="text-success">Eligible for retailer-priced estimates</strong>
                    </>
                  ) : (
                    <>
                      <ShieldAlert size={18} className="text-danger" />
                      <div>
                        <strong className="text-danger">Not eligible — missing required acronyms:</strong>
                        <span className="ms-2">{draftEligibility.missing.join(', ')}</span>
                      </div>
                    </>
                  )}
                </div>

                <fieldset disabled={!canEdit || saving || isMsrp}>
                  <h6 className="fw-bold mb-2">Retailer details</h6>
                  <div className="row g-2 mb-3">
                    <div className="col-sm-8">
                      <label className="form-label small fw-semibold mb-1">Name</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={retailer.name}
                        maxLength={255}
                        onChange={e => updateField('name', e.target.value)}
                      />
                    </div>
                    <div className="col-sm-2 d-flex align-items-end">
                      <div className="form-check form-switch">
                        <input
                          id="ret-national"
                          type="checkbox"
                          className="form-check-input"
                          role="switch"
                          checked={retailer.isNational}
                          onChange={e => updateField('isNational', e.target.checked)}
                        />
                        <label className="form-check-label small fw-semibold" htmlFor="ret-national">National</label>
                      </div>
                    </div>
                    <div className="col-sm-2 d-flex align-items-end">
                      <div className="form-check form-switch">
                        <input
                          id="ret-active"
                          type="checkbox"
                          className="form-check-input"
                          role="switch"
                          checked={retailer.isActive}
                          onChange={e => updateField('isActive', e.target.checked)}
                        />
                        <label className="form-check-label small fw-semibold" htmlFor="ret-active">Active</label>
                      </div>
                    </div>
                  </div>

                  <div className="row g-2 mb-2">
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold mb-1">Address line 1</label>
                      <input type="text" className="form-control form-control-sm" maxLength={255}
                        value={retailer.addressLine1 ?? ''} onChange={e => updateField('addressLine1', e.target.value || null)} />
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold mb-1">Address line 2</label>
                      <input type="text" className="form-control form-control-sm" maxLength={255}
                        value={retailer.addressLine2 ?? ''} onChange={e => updateField('addressLine2', e.target.value || null)} />
                    </div>
                  </div>

                  <div className="row g-2 mb-2">
                    <div className="col-sm-4">
                      <label className="form-label small fw-semibold mb-1">City</label>
                      <input type="text" className="form-control form-control-sm" maxLength={100}
                        value={retailer.city ?? ''} onChange={e => updateField('city', e.target.value || null)} />
                    </div>
                    <div className="col-sm-3">
                      <label className="form-label small fw-semibold mb-1">State / Province</label>
                      <input type="text" className="form-control form-control-sm" maxLength={100}
                        value={retailer.stateProvince ?? ''} onChange={e => updateField('stateProvince', e.target.value || null)} />
                    </div>
                    <div className="col-sm-2">
                      <label className="form-label small fw-semibold mb-1">Postal</label>
                      <input type="text" className="form-control form-control-sm" maxLength={20}
                        value={retailer.postalCode ?? ''} onChange={e => updateField('postalCode', e.target.value || null)} />
                    </div>
                    <div className="col-sm-3">
                      <label className="form-label small fw-semibold mb-1">Country</label>
                      <input type="text" className="form-control form-control-sm" maxLength={100}
                        value={retailer.country ?? ''} onChange={e => updateField('country', e.target.value || null)} />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label small fw-semibold mb-1">Currencies supported</label>
                    <div className="d-flex gap-3">
                      {(['USD', 'CAD'] as CurrencyCode[]).map(code => {
                        const checked = retailer.currenciesSupported.includes(code);
                        return (
                          <div key={code} className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`cur-${code}`}
                              checked={checked}
                              onChange={e => {
                                const next = e.target.checked
                                  ? [...retailer.currenciesSupported.filter(c => c !== code), code]
                                  : retailer.currenciesSupported.filter(c => c !== code);
                                // Preserve canonical order CAD-then-USD for stable equality.
                                const ordered = (['CAD', 'USD'] as CurrencyCode[]).filter(c => next.includes(c));
                                updateField('currenciesSupported', ordered);
                              }}
                            />
                            <label className="form-check-label small" htmlFor={`cur-${code}`}>{code}</label>
                          </div>
                        );
                      })}
                    </div>
                    <div className="form-text small">
                      Retailer-wide. When a customer's currency isn't supported,
                      the estimator falls back to MSRP for this retailer.
                    </div>
                  </div>

                  {/* Portal language — which locales the switcher offers, and
                      which one the portal opens in. Both only take effect on
                      this retailer's branded subdomain (the apex resolves to
                      Trusscore #1). Nothing ticked = every active locale. */}
                  <div className="border rounded-3 p-3 mb-3 bg-light">
                    <label className="form-label small fw-semibold mb-2">Portal language</label>
                    {locales.length === 0 ? (
                      <div className="form-text small">
                        No active languages found — manage them under Admin &rsaquo; Languages.
                      </div>
                    ) : (() => {
                      const supported = retailer.localesSupported ?? [];
                      const restricted = supported.length > 0;
                      // The default must be one the portal actually offers.
                      const defaultChoices = restricted
                        ? locales.filter(l => supported.includes(l.code))
                        : locales;
                      const defaultOrphaned = !!retailer.defaultLocale
                        && !defaultChoices.some(l => l.code === retailer.defaultLocale);
                      return (
                        <>
                          <div className="mb-3">
                            <label className="form-label small fw-semibold mb-1">Languages offered</label>
                            <div className="d-flex flex-wrap gap-3">
                              {locales.map(l => {
                                const checked = supported.includes(l.code);
                                return (
                                  <div key={l.code} className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id={`loc-${l.code}`}
                                      checked={checked}
                                      onChange={e => {
                                        const next = e.target.checked
                                          ? [...supported, l.code]
                                          : supported.filter(c => c !== l.code);
                                        // Registry order for stable equality; empty stores as
                                        // null, which means "all" rather than "none".
                                        const ordered = locales.map(x => x.code).filter(c => next.includes(c));
                                        updateField('localesSupported', ordered.length ? ordered : null);
                                        // Don't leave a default pointing at a language just removed.
                                        if (ordered.length && retailer.defaultLocale && !ordered.includes(retailer.defaultLocale)) {
                                          updateField('defaultLocale', null);
                                        }
                                      }}
                                    />
                                    <label className="form-check-label small" htmlFor={`loc-${l.code}`}>
                                      {l.englishName} <span className="text-muted">({l.code})</span>
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="form-text small">
                              {restricted
                                ? 'Only the ticked languages appear in the switcher on this portal.'
                                : 'Nothing ticked — every active language is offered.'}
                            </div>
                          </div>

                          <div style={{ maxWidth: 320 }}>
                            <label className="form-label small fw-semibold mb-1">Opens in</label>
                            <select
                              className={`form-select form-select-sm${defaultOrphaned ? ' is-invalid' : ''}`}
                              value={retailer.defaultLocale ?? ''}
                              onChange={e => updateField('defaultLocale', e.target.value || null)}
                            >
                              <option value="">Global default (visitor&rsquo;s browser language)</option>
                              {defaultChoices.map(l => (
                                <option key={l.code} value={l.code}>{l.englishName} ({l.code})</option>
                              ))}
                            </select>
                            <div className="form-text small">
                              The language a first-time visitor sees before choosing. A visitor&rsquo;s
                              own choice, and a language referral link, still win over this.
                            </div>
                            {defaultOrphaned && (
                              <div className="invalid-feedback d-block small">
                                Current default ({retailer.defaultLocale}) is not offered on this portal.
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Estimate total display — per-retailer overrides */}
                  <div className="border rounded-3 p-3 mb-3 bg-light">
                    <label className="form-label small fw-semibold mb-2">Estimate totals</label>
                    <div className="form-check form-switch mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        role="switch"
                        id="ret-show-totals"
                        checked={retailer.showTotals}
                        onChange={e => updateField('showTotals', e.target.checked)}
                      />
                      <label className="form-check-label small" htmlFor="ret-show-totals">
                        Always show price &amp; total columns
                      </label>
                      <div className="form-text small">
                        Forces Unit Price / Total columns on for every <strong>shared estimate and PDF</strong> of
                        this retailer, regardless of the per-estimate setting. (Does not affect the live estimator.)
                      </div>
                    </div>
                    <div style={{ maxWidth: 280 }}>
                      <label className="form-label small fw-semibold mb-1">Estimated total format</label>
                      <select
                        className="form-select form-select-sm"
                        value={retailer.totalFormat}
                        onChange={e => updateField('totalFormat', e.target.value as 'rounded' | 'precise')}
                      >
                        <option value="rounded">Rounded up to nearest $100 (no decimals)</option>
                        <option value="precise">Precise (exact, 2 decimals)</option>
                      </select>
                      <div className="form-text small">Controls the main &ldquo;Estimated Total&rdquo; figure.</div>
                    </div>
                  </div>

                  {/* Guest capture + lead routing — per-retailer overrides */}
                  <div className="border rounded-3 p-3 mb-3 bg-light">
                    <label className="form-label small fw-semibold mb-2">Guest capture</label>

                    <div style={{ maxWidth: 420 }} className="mb-3">
                      <label className="form-label small fw-semibold mb-1">What guests are asked for</label>
                      <select
                        className="form-select form-select-sm"
                        value={retailer.captureMode}
                        onChange={e => updateField('captureMode', e.target.value as CaptureMode)}
                      >
                        <option value="full">Full — name, phone, address (materials list gated)</option>
                        <option value="minimal">Minimal — estimate name + email only (nothing gated)</option>
                      </select>
                      <div className="form-text small">
                        <strong>Minimal</strong> asks only for an estimate name and an email, and shows the
                        full materials list whether the guest saves or not — saving just emails them a private
                        link they can return to. Use it where a partner objects to collecting personal
                        information. <strong>Full</strong> is today&rsquo;s ten-field form and keeps the
                        detailed list behind it.
                      </div>
                    </div>

                    <div style={{ maxWidth: 560 }} className="mb-3">
                      <label className="form-label small fw-semibold mb-1">Pardot form handler URL</label>
                      <input
                        type="url"
                        className="form-control form-control-sm"
                        maxLength={500}
                        placeholder="Leave blank to use the global handler"
                        value={retailer.pardotFormHandlerUrl ?? ''}
                        onChange={e => updateField('pardotFormHandlerUrl', e.target.value || null)}
                      />
                      <div className="form-text small">
                        Sends this retailer&rsquo;s leads to their own handler instead of the global one.
                        <strong> Minimal capture usually needs this</strong> — the global handler has required
                        fields that reject an email-only lead. With no handler here and none set globally, the
                        Pardot submission is skipped entirely.
                      </div>
                    </div>

                    <div style={{ maxWidth: 560 }} className="mb-3">
                      <label className="form-label small fw-semibold mb-1">On-behalf form handler URL</label>
                      <input
                        type="url"
                        className="form-control form-control-sm"
                        maxLength={500}
                        placeholder="Leave blank to use the global on-behalf handler"
                        value={retailer.pardotOnBehalfHandlerUrl ?? ''}
                        onChange={e => updateField('pardotOnBehalfHandlerUrl', e.target.value || null)}
                      />
                      <div className="form-text small">
                        Where this retailer&rsquo;s staff&rsquo;s <strong>save for a customer</strong> submissions go
                        (customer details + estimate link + prepared-by fields). Pardot emails the customer from there.
                      </div>
                    </div>

                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        role="switch"
                        id="ret-accounts-enabled"
                        checked={retailer.accountsEnabled}
                        onChange={e => updateField('accountsEnabled', e.target.checked)}
                      />
                      <label className="form-check-label small" htmlFor="ret-accounts-enabled">
                        Allow user accounts on this retailer&rsquo;s portal
                      </label>
                      <div className="form-text small">
                        Off hides sign-in and create-account everywhere on this portal. Guests can still save
                        estimates, and can still claim them later by creating an account on any other portal —
                        claiming matches on the verified email address alone.
                      </div>
                    </div>
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-sm-3">
                      <label className="form-label small fw-semibold mb-1">Phone</label>
                      <input type="text" className="form-control form-control-sm" maxLength={50}
                        value={retailer.phone ?? ''} onChange={e => updateField('phone', e.target.value || null)} />
                    </div>
                    <div className="col-sm-4">
                      <label className="form-label small fw-semibold mb-1">Email</label>
                      <input type="email" className="form-control form-control-sm" maxLength={255}
                        value={retailer.email ?? ''} onChange={e => updateField('email', e.target.value || null)} />
                    </div>
                    <div className="col-sm-5">
                      <label className="form-label small fw-semibold mb-1">Website</label>
                      <input type="url" className="form-control form-control-sm" maxLength={500}
                        value={retailer.website ?? ''} onChange={e => updateField('website', e.target.value || null)} />
                    </div>
                  </div>

                  {/* Hierarchy, staff-account matching + Salesforce mapping. */}
                  {!isMsrp && (
                    <RetailerAccountsSection
                      retailer={retailer}
                      retailers={allRetailers}
                      isCreating={isCreating}
                      onChange={updateField}
                    />
                  )}

                  {/* Live, read-only picture: parent / children / linked stores / members and their view. */}

                  {/* Partner Branding (super admin only — collapsed by default
                      unless retailer is already a partner). */}
                  {!isMsrp && (
                    <PartnerBrandingSection
                      retailer={retailer}
                      eligible={draftEligibility.eligible}
                      onChange={updateField}
                    />
                  )}
                </fieldset>

                {/* Brand icon — any retailer INCLUDING Trusscore (#1), which is
                    why it sits outside the MSRP-locked fieldset above: apex
                    estimates show Trusscore's icon in the admin lists. On #1
                    the normal Save button is hidden, so the icon saves on its own. */}
                <fieldset disabled={!canEdit || saving || iconSaving}>
                  <div className="row g-2 mb-3">
                    <div className="col-sm-8">
                      <label className="form-label small fw-semibold mb-1 d-flex align-items-center gap-1">
                        Brand icon
                        <Tooltip content={BRAND_ICON_HELP} position="right" />
                      </label>
                      <ImageUploadField
                        value={retailer.brandIconUrl}
                        onChange={v => updateField('brandIconUrl', v)}
                        retailerId={retailer.id}
                        kind="brand-icon"
                        aspectPresets={BRAND_ICON_ASPECTS}
                        maxOutputPx={512}
                        placeholder="/uploads/partners/brand-icon-...png"
                        helpText={isMsrp
                          ? 'Trusscore\'s own mark is built in and shows for estimates created on the Trusscore site. Upload here only to replace it.'
                          : 'Square, 64–512 px. PNG, SVG, JPG or WebP. Shown beside this retailer\'s estimates in admin lists.'}
                        previewBg="#ffffff"
                      />
                    </div>
                    <div className="col-sm-4 d-flex align-items-end gap-2 pb-2">
                      {brandIconFor(retailer.id, retailer.brandIconUrl) && (
                        <>
                          <span className="small text-muted">{retailer.brandIconUrl ? 'In lists:' : 'Default:'}</span>
                          <img src={brandIconFor(retailer.id, retailer.brandIconUrl) ?? undefined} alt="" width={24} height={24}
                            style={{ objectFit: 'contain', borderRadius: 4, border: '1px solid #dee2e6', background: '#fff' }} />
                          <img src={brandIconFor(retailer.id, retailer.brandIconUrl) ?? undefined} alt="" width={40} height={40}
                            style={{ objectFit: 'contain', borderRadius: 6, border: '1px solid #dee2e6', background: '#fff' }} />
                        </>
                      )}
                      {isMsrp && canEdit && (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm ms-auto d-inline-flex align-items-center gap-1"
                          onClick={saveIconOnly}
                          disabled={iconSaving}
                        >
                          {iconSaving ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={13} />} Save icon
                        </button>
                      )}
                    </div>
                  </div>
                </fieldset>

                {/* Products area lives in its own fieldset so the MSRP master
                    (retailer #1) stays read-only WITHOUT disabling the product
                    filter. Every per-row control already enforces isMsrp on its
                    own, so we only gate this fieldset on canEdit / saving. */}
                <fieldset disabled={!canEdit || saving}>
                  {/* Products section */}
                  {isCreating && retailer.pricingSource === 'parent' ? (
                    <div className="alert alert-info py-2 px-3 small mt-4 mb-2">
                      This retailer will use its parent&apos;s price book, so no products are attached to it.
                      Manage products and prices on the parent retailer.
                    </div>
                  ) : isCreating ? (
                    <>
                      <h6 className="fw-bold mt-4 mb-2">Initial product set</h6>
                      <p className="text-muted small mb-2">
                        New retailer's products inherit MSRP pricing,
                        <strong> but with active=off and a review flag</strong> so the
                        retailer admin reviews each before going live.
                      </p>
                      <div className="d-flex gap-3 mb-2">
                        <div className="form-check">
                          <input type="radio" className="form-check-input" id="rm-all"
                            checked={createMode === 'all'} onChange={() => setCreateMode('all')} />
                          <label className="form-check-label small" htmlFor="rm-all">Add all products</label>
                        </div>
                        <div className="form-check">
                          <input type="radio" className="form-check-input" id="rm-subset"
                            checked={createMode === 'subset'} onChange={() => setCreateMode('subset')} />
                          <label className="form-check-label small" htmlFor="rm-subset">Select products</label>
                        </div>
                        <div className="form-check">
                          <input type="radio" className="form-check-input" id="rm-none"
                            checked={createMode === 'none'} onChange={() => setCreateMode('none')} />
                          <label className="form-check-label small" htmlFor="rm-none">None for now</label>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-4 mb-2">
                        <h6 className="fw-bold mb-0">
                          {pricingInherited
                            ? <>Products carried <span className="text-muted fw-normal">(from {pricingInfo?.priceBookRetailerName ?? 'the parent retailer'})</span></>
                            : 'Products carried'}
                        </h6>
                        {retailerId && retailerId !== 1 && !pricingInherited && (
                          <div className="d-flex align-items-center gap-2">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                              onClick={handleExportPrices}
                              disabled={exportingPrices}
                              title="Download this retailer's prices as an Excel file"
                            >
                              {exportingPrices ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={13} />} Export prices
                            </button>
                            {canEdit && (
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                                onClick={() => setPriceImportOpen(true)}
                                title="Import an edited price file (this retailer only)"
                              >
                                <Upload size={13} /> Import prices
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {pricingInherited ? (
                        <div className="alert alert-info py-2 px-3 small mb-2">
                          Prices come from <strong>{pricingInfo?.priceBookRetailerName ?? 'the parent retailer'}</strong> and
                          are shown read-only here. Edit products and prices on that retailer, or turn off
                          &quot;Inherit parent&apos;s pricing&quot; above to give this location its own price book.
                        </div>
                      ) : (
                        <p className="text-muted small mb-2">
                          Toggle to add or remove products from this retailer.
                          Newly-added rows arrive at MSRP pricing with active=off.
                        </p>
                      )}
                    </>
                  )}

                  {(isCreating ? (createMode === 'subset' && retailer.pricingSource !== 'parent') : true) && products.length > 0 && (
                    <ProductChecklist
                      products={filteredProducts}
                      isCreating={isCreating}
                      createSelected={createSelected}
                      carriedDraft={carriedDraft}
                      rowEdits={rowEdits}
                      filter={productFilter}
                      onFilterChange={setProductFilter}
                      onToggleCreate={(id, checked) => {
                        const next = new Set(createSelected);
                        if (checked) next.add(id); else next.delete(id);
                        setCreateSelected(next);
                      }}
                      onToggleEdit={(id, checked) => {
                        const next = new Map(carriedDraft);
                        next.set(id, checked);
                        setCarriedDraft(next);
                      }}
                      onPatchRow={patchRow}
                      onEditManualPrice={editManualPrice}
                      onBulkApplyMarkup={(currency, percent) => {
                        // Drop the markup onto every carried row; price
                        // reset is computed on save.
                        setRowEdits(prev => {
                          const next = new Map(prev);
                          products.forEach(p => {
                            const drafted = carriedDraft.get(p.productId) ?? p.carried;
                            if (!drafted || !p.productActive) return;
                            const existing = next.get(p.productId) ?? {};
                            next.set(p.productId, {
                              ...existing,
                              ...(currency === 'USD'
                                ? { percentMarkupUsd: percent, markupClearedByPriceUsd: false }
                                : { percentMarkupCad: percent, markupClearedByPriceCad: false }),
                            });
                          });
                          return next;
                        });
                      }}
                      onBulkClearMarkup={(currency) => {
                        setRowEdits(prev => {
                          const next = new Map(prev);
                          products.forEach(p => {
                            const drafted = carriedDraft.get(p.productId) ?? p.carried;
                            if (!drafted) return;
                            const serverMarkup = currency === 'USD' ? p.percentMarkupUsd : p.percentMarkupCad;
                            const existing = next.get(p.productId) ?? {};
                            const draftedMarkup = currency === 'USD' ? existing.percentMarkupUsd : existing.percentMarkupCad;
                            const effective = draftedMarkup !== undefined ? draftedMarkup : (serverMarkup ?? null);
                            if (effective === null) return; // nothing to clear
                            next.set(p.productId, {
                              ...existing,
                              ...(currency === 'USD'
                                ? { percentMarkupUsd: null }
                                : { percentMarkupCad: null }),
                            });
                          });
                          return next;
                        });
                      }}
                      onBulkClearReview={() => {
                        // Clear needs_review on every carried row that's
                        // currently flagged. Only carried products are
                        // candidates; inactive/uncarried rows skip.
                        setRowEdits(prev => {
                          const next = new Map(prev);
                          products.forEach(p => {
                            const drafted = carriedDraft.get(p.productId) ?? p.carried;
                            if (!drafted) return;
                            const currentReview = next.get(p.productId)?.needsReview ?? p.needsReview;
                            if (currentReview) {
                              next.set(p.productId, { ...(next.get(p.productId) ?? {}), needsReview: false });
                            }
                          });
                          return next;
                        });
                      }}
                      onBulkActivate={() => {
                        setRowEdits(prev => {
                          const next = new Map(prev);
                          products.forEach(p => {
                            const drafted = carriedDraft.get(p.productId) ?? p.carried;
                            if (!drafted) return;
                            const currentActive = next.get(p.productId)?.isActive ?? p.isActive;
                            if (!currentActive) {
                              next.set(p.productId, { ...(next.get(p.productId) ?? {}), isActive: true });
                            }
                          });
                          return next;
                        });
                      }}
                      requiredAcronyms={requiredAcronyms}
                      isMsrp={gridReadOnly}
                      fieldDefs={fieldDefs}
                      customFieldEdits={customFieldEdits}
                      onPatchCustomField={patchCustomField}
                    />
                  )}

                  {/* Delete (edit only, non-MSRP, super) */}
                  {!isCreating && canEdit && !isMsrp && (
                    <div className="border-top pt-3 mt-4">
                      {!confirmDelete ? (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1"
                          onClick={() => setConfirmDelete(true)}
                        >
                          <Trash2 size={14} /> Delete Retailer
                        </button>
                      ) : (
                        <div className="border border-danger rounded-3 p-3 bg-danger-subtle">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <AlertTriangle size={18} className="text-danger" />
                            <strong className="text-danger">Delete this retailer permanently?</strong>
                          </div>
                          <ul className="small mb-2 ps-3">
                            <li>{usage.userCount} user assignment{usage.userCount === 1 ? '' : 's'} will lose their retailer link.</li>
                            <li>{usage.shareCount} shared estimate{usage.shareCount === 1 ? '' : 's'} will display a "retailer no longer available" banner. Pricing already snapshotted into the estimate is preserved.</li>
                            <li>All <strong>{retailer.name}</strong> retailer_products rows are wiped.</li>
                          </ul>
                          <p className="small mb-2">
                            To confirm, type the retailer name <code className="fw-bold">{retailer.name}</code>:
                          </p>
                          <input
                            type="text"
                            className="form-control form-control-sm mb-2"
                            value={deleteNameInput}
                            onChange={e => setDeleteNameInput(e.target.value)}
                            placeholder="Retailer name"
                          />
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => { setConfirmDelete(false); setDeleteNameInput(''); }}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm d-inline-flex align-items-center gap-1"
                              disabled={!nameMatches || saving}
                              onClick={onDeleted}
                            >
                              <Trash2 size={14} /> Confirm Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </fieldset>
              </>
            )}
          </div>

          <div className="modal-footer bg-light">
            <button type="button" className="btn btn-outline-secondary" onClick={handleClose} disabled={saving}>
              {canEdit && !isMsrp ? 'Cancel' : 'Close'}
            </button>
            {canEdit && !isMsrp && (
              <button
                type="button"
                className="btn btn-primary d-inline-flex align-items-center gap-1"
                onClick={handleSave}
                disabled={saving || loading}
              >
                {saving ? (
                  <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
                ) : (
                  <><Save size={16} /> {isCreating ? 'Create Retailer' : 'Save Changes'}</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Pre-save markup confirmation. Shown once per save attempt when
          markup-set edits are present. Accepting it sets a flag so the
          next handleSave call bypasses the gate and commits. */}
      {pendingMarkupConfirm && (
        <div
          className="modal d-block fade show"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1080 }}
          tabIndex={-1}
          onClick={(e) => { if (e.target === e.currentTarget) setPendingMarkupConfirm(null); }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                  <AlertTriangle size={18} className="text-warning" />
                  Reset prices from MSRP?
                </h5>
              </div>
              <div className="modal-body">
                <p className="small mb-2">
                  Saving will <strong>reset the stored price</strong> for the
                  rows below to <code>MSRP × (1 + markup/100)</code>. The
                  markup is then stored, and any future MSRP change will
                  recompute these prices automatically. Manual price edits
                  on a row will clear that row's markup.
                </p>
                <div className="border rounded-3" style={{ maxHeight: 280, overflowY: 'auto' }}>
                  <table className="table table-sm mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>SKU</th>
                        <th className="text-end">USD %</th>
                        <th className="text-end">CAD %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingMarkupConfirm.rows.map(r => (
                        <tr key={r.productId}>
                          <td className="font-monospace small">{r.sku}</td>
                          <td className="text-end small">{r.usd !== null ? `${r.usd > 0 ? '+' : ''}${r.usd.toFixed(2)}%` : '—'}</td>
                          <td className="text-end small">{r.cad !== null ? `${r.cad > 0 ? '+' : ''}${r.cad.toFixed(2)}%` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setPendingMarkupConfirm(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning d-inline-flex align-items-center gap-1"
                  onClick={() => handleSave()}
                  disabled={saving}
                >
                  {saving ? (
                    <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
                  ) : (
                    <><Percent size={16} /> Apply markups & save</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {priceImportOpen && retailerId && retailer && (
        <AdminImportModal
          mode="retailer"
          retailerId={retailerId}
          retailerName={retailer.name}
          onClose={() => setPriceImportOpen(false)}
          onImported={reloadDetail}
        />
      )}
    </div>
  );
};

// ============================================================================
// Partner Branding section
//
// Collapsible block in the retailer modal that captures everything needed
// to serve this retailer at <subdomain>.estimator.trusscore.com with custom
// branding: subdomain, theme colors, logo, splash, nav links, and the
// under-construction message shown when product eligibility fails.
//
// All fields write back through the parent's updateField helper, keeping
// the modal's draft state as the single source of truth right up to save.
// ============================================================================

const RESERVED_SUBDOMAINS = new Set([
  'estimator', 'beta', 'www', 'admin', 'api',
  'mail', 'ftp', 'webmail', 'cpanel', 'webdisk',
  'ns1', 'ns2', 'mx', 'localhost', 'staging',
  'dev', 'test', 'demo', 'old', 'new',
]);

interface PartnerBrandingProps {
  retailer: AdminRetailer;
  eligible: boolean;
  onChange: <K extends keyof AdminRetailer>(key: K, value: AdminRetailer[K]) => void;
}

const PartnerBrandingSection: React.FC<PartnerBrandingProps> = ({ retailer, eligible, onChange }) => {
  // Auto-expand if this retailer already has any branding data populated;
  // otherwise stay collapsed so the modal isn't overwhelming for retailers
  // that aren't partners.
  const hasBranding = retailer.isPartner || !!retailer.subdomain || !!retailer.theme || !!retailer.logoUrl;
  const [open, setOpen] = useState(hasBranding);

  // Subdomain validation. Empty is allowed (means "not assigned"). Otherwise
  // must be a valid DNS label and not in the reserved set. Surfaces inline
  // so the user can fix before save.
  const subdomainError = useMemo(() => {
    const s = retailer.subdomain?.trim() ?? '';
    if (s === '') return null;
    if (s.length > 63) return 'Too long (max 63 characters)';
    if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(s)) {
      return 'Lowercase letters, digits, and hyphens only (no leading/trailing hyphen)';
    }
    if (RESERVED_SUBDOMAINS.has(s)) return `'${s}' is reserved`;
    return null;
  }, [retailer.subdomain]);

  const subdomainPreview = retailer.subdomain?.trim() || '<subdomain>';
  const canEnablePartner = !!retailer.subdomain && !subdomainError;

  // --- Theme helpers -------------------------------------------------------
  const theme = retailer.theme ?? {};
  const updateThemeKey = (key: keyof RetailerTheme, value: string | null) => {
    const next: RetailerTheme = { ...theme };
    if (value === null || value === '') delete next[key];
    else next[key] = value;
    onChange('theme', Object.keys(next).length === 0 ? null : next);
  };

  // --- Splash helpers ------------------------------------------------------
  const splash = retailer.splashConfig ?? {};
  const updateSplashKey = (key: keyof RetailerSplashConfig, value: string | null) => {
    const next: RetailerSplashConfig = { ...splash };
    if (value === null || value === '') delete next[key];
    else next[key] = value;
    onChange('splashConfig', Object.keys(next).length === 0 ? null : next);
  };

  // --- Nav link helpers ----------------------------------------------------
  const navLinks = retailer.navLinks ?? [];
  const updateNavLinks = (next: RetailerNavLink[]) => {
    onChange('navLinks', next.length === 0 ? null : next);
  };
  const addNavLink = () => {
    if (navLinks.length >= 10) return;
    updateNavLinks([
      ...navLinks,
      { label: '', url: '', target: '_blank', showOnEstimator: true, showOnDesign: true },
    ]);
  };
  const patchNavLink = (idx: number, patch: Partial<RetailerNavLink>) => {
    updateNavLinks(navLinks.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const removeNavLink = (idx: number) => {
    updateNavLinks(navLinks.filter((_, i) => i !== idx));
  };

  // Eligibility warning shown only when the retailer is flagged as a
  // partner but the live eligibility check is failing — the customer-
  // facing site will fall back to "Under Construction" until missing
  // products are activated.
  const showEligibilityWarning = retailer.isPartner && !eligible;

  return (
    <div className="border rounded-3 mb-3 mt-4">
      <button
        type="button"
        className="btn btn-link p-0 w-100 d-flex align-items-center gap-2 px-3 py-2 text-decoration-none text-dark"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <Sparkles size={16} className="text-primary" />
        <strong>Partner Branding</strong>
        {retailer.isPartner && (
          <span className="badge text-bg-primary ms-1">Partner</span>
        )}
        {retailer.subdomain && (
          <span className="text-muted small ms-2 font-monospace">
            {retailer.subdomain}.estimator.trusscore.com
          </span>
        )}
        <span className="ms-auto text-muted small">{open ? 'Collapse' : 'Expand'}</span>
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 border-top">
          <p className="text-muted small mb-3">
            Configure the branded subdomain experience. When <strong>Is Partner</strong>
            is enabled and product eligibility passes, this retailer is served at the
            subdomain below with the theme, logo, splash, and nav links defined here.
          </p>

          {showEligibilityWarning && (
            <div className="alert alert-warning py-2 px-3 small d-flex align-items-center gap-2">
              <AlertTriangle size={14} />
              Partner is enabled but eligibility is failing — the partner subdomain
              will show the Under Construction page until missing products are
              activated.
            </div>
          )}

          {/* --- Subdomain + master toggle ------------------------------ */}
          <div className="row g-2 mb-3">
            <div className="col-md-7">
              <label className="form-label small fw-semibold mb-1">Subdomain</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text">https://</span>
                <input
                  type="text"
                  className={`form-control ${subdomainError ? 'is-invalid' : ''}`}
                  value={retailer.subdomain ?? ''}
                  maxLength={63}
                  placeholder="homedepotcan"
                  onChange={e => onChange('subdomain', e.target.value.toLowerCase().trim() || null)}
                />
                <span className="input-group-text">.estimator.trusscore.com</span>
              </div>
              {subdomainError ? (
                <div className="invalid-feedback d-block small">{subdomainError}</div>
              ) : retailer.subdomain ? (
                <div className="form-text small d-flex align-items-center gap-1">
                  <ExternalLink size={11} />
                  Will be served at{' '}
                  <code>https://{subdomainPreview}.estimator.trusscore.com</code>
                </div>
              ) : (
                <div className="form-text small">
                  Lowercase letters, digits, and hyphens. Leave blank to disable
                  partner branding entirely.
                </div>
              )}
            </div>
            <div className="col-md-5 d-flex align-items-end">
              <div className="form-check form-switch">
                <input
                  id="ret-partner"
                  type="checkbox"
                  className="form-check-input"
                  role="switch"
                  checked={retailer.isPartner}
                  disabled={!canEnablePartner}
                  onChange={e => onChange('isPartner', e.target.checked)}
                />
                <label className="form-check-label small fw-semibold" htmlFor="ret-partner">
                  Is Partner
                  {!canEnablePartner && (
                    <span className="text-muted ms-2">(set a subdomain first)</span>
                  )}
                </label>
              </div>
            </div>
          </div>

          {/* --- Theme color pickers ------------------------------------ */}
          <h6 className="fw-bold small mt-3 mb-2">Theme colors</h6>
          <p className="text-muted small mb-2">
            Empty fields fall back to the default Trusscore palette at runtime.
            Click the trash icon next to a swatch to clear and revert to default.
          </p>
          {THEME_GROUPS.map((group) => (
            <div key={group.label} className="mb-3">
              <div className="fw-semibold small mb-1 text-uppercase text-muted" style={{ letterSpacing: '0.04em' }}>
                {group.label}
              </div>
              {group.description && (
                <div className="text-muted small mb-2">{group.description}</div>
              )}
              <div className="row g-2">
                {group.fields.map(({ key, label, help }) => {
                  const value = theme[key] ?? '';
                  const fallback = DEFAULT_THEME[key];
                  return (
                    <div key={key} className="col-md-4 col-sm-6">
                      <label className="form-label small fw-semibold mb-1">{label}</label>
                      <div className="input-group input-group-sm">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          style={{ maxWidth: 44 }}
                          value={value || fallback}
                          onChange={e => updateThemeKey(key, e.target.value)}
                          title={help}
                        />
                        <input
                          type="text"
                          className="form-control font-monospace"
                          value={value}
                          placeholder={fallback}
                          maxLength={9}
                          onChange={e => updateThemeKey(key, e.target.value || null)}
                        />
                        {value && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            title="Clear (use default)"
                            onClick={() => updateThemeKey(key, null)}
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                      <div className="form-text small">{help}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* --- Logo --------------------------------------------------- */}
          <h6 className="fw-bold small mt-3 mb-2">Nav logo</h6>
          <div className="row g-2 mb-3">
            <div className="col-md-8">
              <label className="form-label small fw-semibold mb-1">Logo image</label>
              <ImageUploadField
                value={retailer.logoUrl}
                onChange={v => onChange('logoUrl', v)}
                retailerId={retailer.id}
                kind="logo"
                aspectPresets={LOGO_ASPECTS}
                placeholder="/uploads/partners/logo-...png"
                helpText="Upload + crop, or paste a URL. Preview shows against the configured nav background."
                previewBg={theme.navBg ?? DEFAULT_THEME.navBg}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-semibold mb-1">Alt text</label>
              <input
                type="text"
                className="form-control form-control-sm"
                maxLength={255}
                placeholder="Home Depot Canada"
                value={retailer.logoAlt ?? ''}
                onChange={e => onChange('logoAlt', e.target.value || null)}
              />
            </div>
          </div>

          <h6 className="fw-bold small mt-3 mb-2">Menu &amp; welcome splash</h6>
          <div className="alert alert-light border py-2 px-3 small d-flex align-items-center justify-content-between gap-2">
            <span>Menu ordering, dynamic items, dividers, and rich splash content are managed in the centralized editor.</span>
            <a className="btn btn-outline-primary btn-sm text-nowrap" href={`/admin/site-content?retailer=${retailer.id}`}>Open editor</a>
          </div>

          {/* Legacy controls remain in source for backward data shaping, but
              are intentionally not rendered now that the centralized editor
              owns these fields. */}
          {false && <>
          {/* --- Splash ------------------------------------------------- */}
          <h6 className="fw-bold small mt-3 mb-2">Welcome splash</h6>
          <p className="text-muted small mb-2">
            Shown once per session when a customer lands on this partner's
            estimator. Leave all fields blank to use the default Trusscore splash.
          </p>
          <div className="row g-2 mb-3">
            <div className="col-md-8">
              <label className="form-label small fw-semibold mb-1">Splash image</label>
              <ImageUploadField
                value={splash.imageUrl ?? null}
                onChange={v => updateSplashKey('imageUrl', v)}
                retailerId={retailer.id}
                kind="splash"
                aspectPresets={SPLASH_ASPECTS}
                placeholder="/uploads/partners/splash-...jpg"
                helpText="Upload + crop, or paste a URL. Preview uses the splash background color."
                previewBg={splash.backgroundColor ?? '#ffffff'}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-semibold mb-1">Background</label>
              <div className="input-group input-group-sm">
                <input
                  type="color"
                  className="form-control form-control-color"
                  style={{ maxWidth: 44 }}
                  value={splash.backgroundColor ?? '#ffffff'}
                  onChange={e => updateSplashKey('backgroundColor', e.target.value)}
                />
                <input
                  type="text"
                  className="form-control font-monospace"
                  value={splash.backgroundColor ?? ''}
                  placeholder="#ffffff"
                  maxLength={9}
                  onChange={e => updateSplashKey('backgroundColor', e.target.value || null)}
                />
              </div>
            </div>
            <div className="col-md-12">
              <label className="form-label small fw-semibold mb-1">Headline</label>
              <input
                type="text"
                className="form-control form-control-sm"
                maxLength={200}
                placeholder="Plan your project with Home Depot Canada"
                value={splash.headline ?? ''}
                onChange={e => updateSplashKey('headline', e.target.value || null)}
              />
            </div>
            <div className="col-md-12">
              <label className="form-label small fw-semibold mb-1">Body</label>
              <textarea
                className="form-control form-control-sm"
                maxLength={2000}
                rows={3}
                placeholder="Estimate Trusscore wall and ceiling materials with pricing from your local Home Depot."
                value={splash.body ?? ''}
                onChange={e => updateSplashKey('body', e.target.value || null)}
              />
            </div>
            <div className="col-md-5">
              <label className="form-label small fw-semibold mb-1">CTA label (optional)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                maxLength={100}
                placeholder="Visit Home Depot"
                value={splash.ctaLabel ?? ''}
                onChange={e => updateSplashKey('ctaLabel', e.target.value || null)}
              />
            </div>
            <div className="col-md-7">
              <label className="form-label small fw-semibold mb-1">CTA URL (optional)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                maxLength={1000}
                placeholder="https://homedepot.ca"
                value={splash.ctaUrl ?? ''}
                onChange={e => updateSplashKey('ctaUrl', e.target.value || null)}
              />
            </div>
          </div>

          {/* --- Nav links ---------------------------------------------- */}
          <div className="d-flex align-items-center justify-content-between mt-3 mb-2">
            <h6 className="fw-bold small mb-0">Custom nav links</h6>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
              onClick={addNavLink}
              disabled={navLinks.length >= 10}
            >
              <Plus size={12} /> Add link
            </button>
          </div>
          <p className="text-muted small mb-2">
            Up to 10 links shown alongside the built-in estimator nav. Toggle
            visibility per view (estimate wizard vs. design stage).
          </p>
          {navLinks.length === 0 ? (
            <div className="text-muted small fst-italic mb-3">No custom nav links.</div>
          ) : (
            <div className="mb-3">
              {navLinks.map((link, idx) => (
                <div key={idx} className="border rounded-3 p-2 mb-2 bg-light-subtle">
                  <div className="row g-2 align-items-end">
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold mb-1">Label</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        maxLength={100}
                        value={link.label}
                        onChange={e => patchNavLink(idx, { label: e.target.value })}
                      />
                    </div>
                    <div className="col-md-5">
                      <label className="form-label small fw-semibold mb-1">URL</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        maxLength={1000}
                        value={link.url}
                        onChange={e => patchNavLink(idx, { url: e.target.value })}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small fw-semibold mb-1">Target</label>
                      <select
                        className="form-select form-select-sm"
                        value={link.target}
                        onChange={e => patchNavLink(idx, { target: e.target.value as '_self' | '_blank' })}
                      >
                        <option value="_blank">New tab</option>
                        <option value="_self">Same tab</option>
                      </select>
                    </div>
                    <div className="col-md-1 text-end">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeNavLink(idx)}
                        title="Remove link"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                  <div className="d-flex gap-3 mt-2">
                    <div className="form-check">
                      <input
                        id={`nav-${idx}-est`}
                        type="checkbox"
                        className="form-check-input"
                        checked={link.showOnEstimator}
                        onChange={e => patchNavLink(idx, { showOnEstimator: e.target.checked })}
                      />
                      <label className="form-check-label small" htmlFor={`nav-${idx}-est`}>
                        Show on estimator
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        id={`nav-${idx}-design`}
                        type="checkbox"
                        className="form-check-input"
                        checked={link.showOnDesign}
                        onChange={e => patchNavLink(idx, { showOnDesign: e.target.checked })}
                      />
                      <label className="form-check-label small" htmlFor={`nav-${idx}-design`}>
                        Show on design stage
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          </>}

          {/* --- Location map defaults ---------------------------------- */}
          <h6 className="fw-bold small mt-3 mb-2">Location map defaults</h6>
          <p className="text-muted small mb-2">
            Which trusscore.com locations belong to this portal. Any map whose filter has
            &quot;Use the portal&apos;s retailer filter&quot; on narrows to these. Salesforce account
            ids one per line (or comma-separated); buying groups / regions / countries comma-separated.
          </p>
          <div className="row g-2 mb-3">
            <div className="col-12 col-md-6">
              <label className="form-label small mb-1">Salesforce account ids</label>
              <textarea
                className="form-control form-control-sm"
                rows={3}
                placeholder={'0012T00001l1abxQAA\n0012T00001mjVHDQA2'}
                value={(retailer.mapFilter?.ids ?? []).join('\n')}
                onChange={e => {
                  const ids = e.target.value.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
                  const next: NonNullable<AdminRetailer['mapFilter']> = { ...(retailer.mapFilter ?? {}), ids };
                  if (ids.length === 0) delete next.ids;
                  onChange('mapFilter', Object.keys(next).length === 0 ? null : next);
                }}
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label small mb-1">Buying groups</label>
              <input
                className="form-control form-control-sm mb-2"
                placeholder="Home Hardware, Castle"
                value={(retailer.mapFilter?.buyingGroups ?? []).join(', ')}
                onChange={e => {
                  const buyingGroups = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                  const next: NonNullable<AdminRetailer['mapFilter']> = { ...(retailer.mapFilter ?? {}), buyingGroups };
                  if (buyingGroups.length === 0) delete next.buyingGroups;
                  onChange('mapFilter', Object.keys(next).length === 0 ? null : next);
                }}
              />
              <label className="form-label small mb-1">Regions / countries</label>
              <div className="input-group input-group-sm">
                <input
                  className="form-control"
                  placeholder="Regions: ON, QC"
                  value={(retailer.mapFilter?.regions ?? []).join(', ')}
                  onChange={e => {
                    const regions = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                    const next: NonNullable<AdminRetailer['mapFilter']> = { ...(retailer.mapFilter ?? {}), regions };
                    if (regions.length === 0) delete next.regions;
                    onChange('mapFilter', Object.keys(next).length === 0 ? null : next);
                  }}
                />
                <input
                  className="form-control"
                  placeholder="Countries: CA, US"
                  value={(retailer.mapFilter?.countries ?? []).join(', ')}
                  onChange={e => {
                    const countries = e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
                    const next: NonNullable<AdminRetailer['mapFilter']> = { ...(retailer.mapFilter ?? {}), countries };
                    if (countries.length === 0) delete next.countries;
                    onChange('mapFilter', Object.keys(next).length === 0 ? null : next);
                  }}
                />
              </div>
            </div>
          </div>

          {/* --- Under construction message ----------------------------- */}
          <h6 className="fw-bold small mt-3 mb-2">Under Construction message</h6>
          <p className="text-muted small mb-2">
            Optional override shown when product eligibility is failing. Leave
            blank to use default copy. Includes a link back to trusscore.com.
          </p>
          <textarea
            className="form-control form-control-sm mb-3"
            maxLength={5000}
            rows={3}
            placeholder="We're putting the finishing touches on this experience. In the meantime, visit Trusscore.com to learn more."
            value={retailer.underConstructionMessage ?? ''}
            onChange={e => onChange('underConstructionMessage', e.target.value || null)}
          />
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Product checklist sub-component
// ============================================================================

interface RowEditPatch {
  priceUsd?: number;
  priceCad?: number;
  isActive?: boolean;
  needsReview?: boolean;
  percentMarkupUsd?: number | null;
  percentMarkupCad?: number | null;
  markupClearedByPriceUsd?: boolean;
  markupClearedByPriceCad?: boolean;
}

interface ChecklistProps {
  products: RetailerProductRow[];
  isCreating: boolean;
  createSelected: Set<number>;
  carriedDraft: Map<number, boolean>;
  rowEdits: Map<number, RowEditPatch>;
  filter: string;
  onFilterChange: (v: string) => void;
  onToggleCreate: (id: number, checked: boolean) => void;
  onToggleEdit: (id: number, checked: boolean) => void;
  onPatchRow: (productId: number, patch: RowEditPatch) => void;
  onEditManualPrice: (productId: number, currency: CurrencyCode, value: number) => void;
  onBulkApplyMarkup: (currency: CurrencyCode, percent: number) => void;
  onBulkClearMarkup: (currency: CurrencyCode) => void;
  onBulkClearReview: () => void;
  onBulkActivate: () => void;
  requiredAcronyms: string[];
  /** Read-only grid: the MSRP master, or a retailer showing its parent's inherited book. */
  isMsrp: boolean;
  fieldDefs: RetailerFieldDef[];
  customFieldEdits: Map<number, Record<number, string>>;
  onPatchCustomField: (productId: number, fieldDefId: number, value: string) => void;
}

// Resolve the effective value for a given row + field, drafted edit wins.
function effective<K extends keyof RowEditPatch>(
  rowEdits: Map<number, RowEditPatch>,
  productId: number,
  key: K,
  fallback: RowEditPatch[K],
): RowEditPatch[K] {
  const drafted = rowEdits.get(productId)?.[key];
  return drafted !== undefined ? drafted : fallback;
}

const ProductChecklist: React.FC<ChecklistProps> = ({
  products, isCreating, createSelected, carriedDraft, rowEdits, filter, onFilterChange,
  onToggleCreate, onToggleEdit, onPatchRow, onEditManualPrice,
  onBulkApplyMarkup, onBulkClearMarkup, onBulkClearReview, onBulkActivate,
  requiredAcronyms, isMsrp, fieldDefs, customFieldEdits, onPatchCustomField,
}) => {
  const requiredSet = useMemo(() => new Set(requiredAcronyms), [requiredAcronyms]);

  // Which product rows have their custom-field editor expanded.
  const [expandedFields, setExpandedFields] = useState<Set<number>>(new Set());
  const toggleFields = (productId: number) => setExpandedFields(prev => {
    const next = new Set(prev);
    if (next.has(productId)) next.delete(productId); else next.add(productId);
    return next;
  });
  const hasFields = !isCreating && fieldDefs.length > 0;
  // Column span for the custom-field detail sub-row (matches header columns).
  const detailColSpan = isCreating ? 6 : 12;
  // Effective custom value: drafted edit wins over the server snapshot.
  const customValue = (p: RetailerProductRow, defId: number): string => {
    const drafted = customFieldEdits.get(p.productId)?.[defId];
    if (drafted !== undefined) return drafted;
    return p.customFields?.find(cf => cf.fieldDefId === defId)?.value ?? '';
  };

  // Counts surfaced in the bulk action toolbar — only meaningful in edit mode.
  const reviewCount = useMemo(() => {
    if (isCreating) return 0;
    let n = 0;
    products.forEach(p => {
      const drafted = carriedDraft.get(p.productId) ?? p.carried;
      if (!drafted) return;
      const review = effective(rowEdits, p.productId, 'needsReview', !!p.needsReview);
      if (review) n++;
    });
    return n;
  }, [products, carriedDraft, rowEdits, isCreating]);

  const inactiveCount = useMemo(() => {
    if (isCreating) return 0;
    let n = 0;
    products.forEach(p => {
      const drafted = carriedDraft.get(p.productId) ?? p.carried;
      if (!drafted) return;
      const active = effective(rowEdits, p.productId, 'isActive', !!p.isActive);
      if (!active) n++;
    });
    return n;
  }, [products, carriedDraft, rowEdits, isCreating]);

  return (
    <div className="border rounded-3 p-2">
      {/* Row 1 — filter + review/activate bulk actions + count */}
      <div className="d-flex gap-2 align-items-center mb-2 flex-wrap">
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Filter by SKU, name, acronym, color…"
          value={filter}
          onChange={e => onFilterChange(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        {!isCreating && !isMsrp && (
          <>
            <button
              type="button"
              className="btn btn-outline-warning btn-sm d-inline-flex align-items-center gap-1"
              disabled={reviewCount === 0}
              onClick={onBulkClearReview}
              title="Clear the needs-review flag on every carried product"
            >
              <BellOff size={12} /> Clear all review flags ({reviewCount})
            </button>
            <button
              type="button"
              className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-1"
              disabled={inactiveCount === 0}
              onClick={onBulkActivate}
              title="Set every carried product to active"
            >
              <Power size={12} /> Activate all carried ({inactiveCount})
            </button>
          </>
        )}
        <span className="text-muted small ms-auto">{products.length} products</span>
      </div>

      {/* Row 2 — markup bulk actions on their own line so they don't crowd
          the review/activate buttons or wrap awkwardly. */}
      {!isCreating && !isMsrp && (
        <div className="d-flex gap-2 align-items-center mb-2 flex-wrap">
          <span className="text-muted small me-1">Bulk markup:</span>
          {(['USD', 'CAD'] as CurrencyCode[]).map(code => (
            <div key={`mu-${code}`} className="btn-group btn-group-sm" role="group">
              <button
                type="button"
                className="btn btn-outline-primary d-inline-flex align-items-center gap-1"
                title={`Apply a percent markup over MSRP to every carried product's ${code} price`}
                onClick={() => {
                  const raw = window.prompt(`Apply % markup over MSRP to all ${code} prices.\nEnter the percent (e.g. 10.5 for +10.5%, -5 for -5%).\n\nThis will reset every ${code} price to MSRP × (1 + N/100) on save.`);
                  if (raw === null) return;
                  const trimmed = raw.trim();
                  if (trimmed === '') return;
                  const num = Number(trimmed);
                  if (!Number.isFinite(num)) {
                    window.alert('Please enter a valid number.');
                    return;
                  }
                  onBulkApplyMarkup(code, Math.round(num * 100) / 100);
                }}
              >
                <Percent size={12} /> Apply {code} markup…
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary d-inline-flex align-items-center gap-1"
                title={`Clear all ${code} markups (manual prices remain)`}
                onClick={() => onBulkClearMarkup(code)}
              >
                Clear
              </button>
            </div>
          ))}
        </div>
      )}
      <div style={{ maxHeight: 360, overflowY: 'auto' }}>
        <table className="table table-sm table-hover mb-0 align-middle">
          <thead className="sticky-top bg-light">
            <tr>
              <th style={{ width: 40 }}></th>
              <th>SKU</th>
              <th>Name</th>
              <th>Acronym</th>
              <th className="text-end" title="Master MSRP">MSRP USD</th>
              <th className="text-end" title="Master MSRP">MSRP CAD</th>
              {!isCreating && (
                <>
                  <th className="text-end" style={{ minWidth: 100 }}>Retailer USD</th>
                  <th className="text-end" style={{ width: 80 }} title="Per-row USD markup over MSRP. Setting this resets the price to MSRP × (1 + N/100) on save.">% USD</th>
                  <th className="text-end" style={{ minWidth: 100 }}>Retailer CAD</th>
                  <th className="text-end" style={{ width: 80 }} title="Per-row CAD markup over MSRP. Setting this resets the price to MSRP × (1 + N/100) on save.">% CAD</th>
                  <th className="text-center" style={{ width: 90 }}>Active</th>
                  <th className="text-center" style={{ width: 110 }}>Review</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {products.map(p => {
              const checked = isCreating ? createSelected.has(p.productId) : (carriedDraft.get(p.productId) ?? p.carried);
              const required = requiredSet.has(p.acronym);

              const draftedActive = effective(rowEdits, p.productId, 'isActive', !!p.isActive);
              const draftedReview = effective(rowEdits, p.productId, 'needsReview', !!p.needsReview);
              const draftedUsd    = effective(rowEdits, p.productId, 'priceUsd',
                p.priceUsd !== null ? p.priceUsd : (p.msrpUsd ?? 0));
              const draftedCad    = effective(rowEdits, p.productId, 'priceCad',
                p.priceCad !== null ? p.priceCad : (p.msrpCad ?? 0));

              const dirty = rowEdits.has(p.productId);

              // Per-row editing only enabled when the product is actually
              // carried by this retailer (or about to be, via the carried
              // checkbox). Otherwise the inputs would write to a row that
              // doesn't exist server-side.
              const editingEnabled = !isCreating && !isMsrp && checked && p.productActive;

              const fieldsExpanded = expandedFields.has(p.productId);

              return (
                <React.Fragment key={p.productId}>
                <tr className={!p.productActive ? 'text-muted' : (dirty ? 'table-warning' : '')}>
                  <td>
                    <div className="d-flex align-items-center gap-1">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={checked}
                        disabled={isMsrp || !p.productActive}
                        onChange={e => isCreating
                          ? onToggleCreate(p.productId, e.target.checked)
                          : onToggleEdit(p.productId, e.target.checked)
                        }
                      />
                      {hasFields && checked && (
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-none"
                          title="Custom fields"
                          onClick={() => toggleFields(p.productId)}
                        >
                          {fieldsExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="font-monospace small">{p.sku}</td>
                  <td className="small">{p.name}</td>
                  <td className="small">
                    {p.acronym}
                    {required && <span className="badge text-bg-warning ms-1" style={{ fontSize: '0.6em' }}>required</span>}
                  </td>
                  <td className="text-end small">{p.msrpUsd !== null ? `$${p.msrpUsd.toFixed(2)}` : '—'}</td>
                  <td className="text-end small">{p.msrpCad !== null ? `$${p.msrpCad.toFixed(2)}` : '—'}</td>

                  {!isCreating && (() => {
                    const edit = rowEdits.get(p.productId);
                    // Drafted markups: undefined → use server. null → cleared in UI.
                    const draftedMarkupUsd = edit?.percentMarkupUsd !== undefined
                      ? edit.percentMarkupUsd
                      : p.percentMarkupUsd;
                    const draftedMarkupCad = edit?.percentMarkupCad !== undefined
                      ? edit.percentMarkupCad
                      : p.percentMarkupCad;
                    const clearedByPriceUsd = !!edit?.markupClearedByPriceUsd;
                    const clearedByPriceCad = !!edit?.markupClearedByPriceCad;

                    return (
                      <>
                        <td className="text-end small p-1" style={{ position: 'relative' }}>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="form-control form-control-sm text-end"
                            style={{ minWidth: 90 }}
                            disabled={!editingEnabled}
                            value={editingEnabled ? draftedUsd : (p.priceUsd ?? 0)}
                            onChange={e => onEditManualPrice(p.productId, 'USD', Number(e.target.value))}
                          />
                          {clearedByPriceUsd && (
                            <div className="text-warning" style={{ fontSize: '0.65em', lineHeight: 1, marginTop: 2 }}>
                              markup cleared
                            </div>
                          )}
                        </td>
                        <td className="text-end small p-1">
                          <input
                            type="number"
                            step="0.01"
                            className="form-control form-control-sm text-end"
                            style={{ width: 70 }}
                            placeholder="—"
                            disabled={!editingEnabled}
                            value={draftedMarkupUsd ?? ''}
                            onChange={e => {
                              const raw = e.target.value;
                              if (raw === '') {
                                onPatchRow(p.productId, { percentMarkupUsd: null, markupClearedByPriceUsd: false });
                              } else {
                                const n = Number(raw);
                                if (Number.isFinite(n)) {
                                  onPatchRow(p.productId, { percentMarkupUsd: Math.round(n * 100) / 100, markupClearedByPriceUsd: false });
                                }
                              }
                            }}
                          />
                        </td>
                        <td className="text-end small p-1" style={{ position: 'relative' }}>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="form-control form-control-sm text-end"
                            style={{ minWidth: 90 }}
                            disabled={!editingEnabled}
                            value={editingEnabled ? draftedCad : (p.priceCad ?? 0)}
                            onChange={e => onEditManualPrice(p.productId, 'CAD', Number(e.target.value))}
                          />
                          {clearedByPriceCad && (
                            <div className="text-warning" style={{ fontSize: '0.65em', lineHeight: 1, marginTop: 2 }}>
                              markup cleared
                            </div>
                          )}
                        </td>
                        <td className="text-end small p-1">
                          <input
                            type="number"
                            step="0.01"
                            className="form-control form-control-sm text-end"
                            style={{ width: 70 }}
                            placeholder="—"
                            disabled={!editingEnabled}
                            value={draftedMarkupCad ?? ''}
                            onChange={e => {
                              const raw = e.target.value;
                              if (raw === '') {
                                onPatchRow(p.productId, { percentMarkupCad: null, markupClearedByPriceCad: false });
                              } else {
                                const n = Number(raw);
                                if (Number.isFinite(n)) {
                                  onPatchRow(p.productId, { percentMarkupCad: Math.round(n * 100) / 100, markupClearedByPriceCad: false });
                                }
                              }
                            }}
                          />
                        </td>
                        <td className="text-center">
                          <div className="form-check form-switch d-inline-flex m-0">
                            <input
                              type="checkbox"
                              className="form-check-input m-0"
                              role="switch"
                              disabled={!editingEnabled}
                              checked={!!draftedActive}
                              onChange={e => onPatchRow(p.productId, { isActive: e.target.checked })}
                            />
                          </div>
                        </td>
                        <td className="text-center small">
                          {draftedReview ? (
                            <button
                              type="button"
                              className="btn btn-warning btn-sm d-inline-flex align-items-center gap-1 py-0 px-2"
                              disabled={!editingEnabled}
                              title="Clear the review flag for this product"
                              onClick={() => onPatchRow(p.productId, { needsReview: false })}
                            >
                              <BellOff size={11} /> Clear
                            </button>
                          ) : checked && p.carried ? (
                            <span className="text-success small">Reviewed</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </>
                    );
                  })()}
                </tr>
                {hasFields && fieldsExpanded && (
                  <tr className="table-light">
                    <td colSpan={detailColSpan}>
                      <div className="d-flex flex-wrap gap-3 py-1 px-2">
                        {fieldDefs.map(d => (
                          <div key={d.id} className="d-flex align-items-center gap-2">
                            <label className="small fw-semibold mb-0">
                              {d.label}
                              {!d.isActive && <span className="badge text-bg-secondary ms-1" style={{ fontSize: '0.6rem' }}>off</span>}
                            </label>
                            <input
                              type={d.dataType === 'numeric' ? 'number' : 'text'}
                              className="form-control form-control-sm"
                              style={{ maxWidth: 200 }}
                              disabled={isMsrp}
                              value={customValue(p, d.id)}
                              onChange={e => onPatchCustomField(p.productId, d.id, e.target.value)}
                              placeholder={d.dataType === 'url' ? 'value or URL' : ''}
                            />
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {!isCreating && !isMsrp && (
        <p className="text-muted small mt-2 mb-0">
          <Plus size={12} className="me-1" /> Adding attaches at MSRP with active=off and review=on.
          <Trash2 size={12} className="ms-2 me-1" /> Removing wipes the row for this retailer only.
          <span className="ms-2">Edited rows highlight in yellow; changes save on <strong>Save Changes</strong>.</span>
        </p>
      )}
    </div>
  );
};

export default AdminRetailerEditModal;
